import { create } from 'zustand'
import { sessionUserId, supabase } from '@/lib/supabase'
import { shared } from '@/lib/inflight'
import { isNetworkFailure } from '@/lib/outbox'
import { getVerseForDate, hashString, mulberry32 } from '@/data/bible/questions'
import { todayLocalDate } from '@/lib/date'
import { localdb } from '@/lib/localdb'
import { applyPlayLocal } from '@/lib/progress'
import { useAuth } from './auth'
import type { DailyVerse, DailyPulse, PlayResult, SubmitOutcome } from '@/types'

interface GameState {
  today: DailyVerse | null
  todayDate: string
  playedToday: boolean
  lastResult: { result: PlayResult; outcome: SubmitOutcome } | null
  pulse: DailyPulse | null
  /** Whether the player armed an XP Boost for today's daily (consumed on submit). */
  boostArmed: boolean

  loadToday: () => Promise<void>
  submitPlay: (result: PlayResult) => Promise<SubmitOutcome>
  loadPulse: () => Promise<void>
  armBoost: (v: boolean) => void
  /** Send any daily play that finished offline. Safe to call any time. */
  flushPending: () => Promise<void>
}

// A daily drop finished with no connection used to be LOST: submit_play threw,
// the recap had nothing to show, and the streak that play kept alive broke at
// midnight. Now a play that fails for want of a NETWORK (never one the server
// refused) waits here, per account, until a connection comes back — sent on
// the next Play-tab visit and on the browser's 'online' event. submit_play
// takes the drop date and works the streak out from it, so a play sent the
// next morning still counts for the day it was played; it is idempotent per
// (user, date), so sending twice is harmless. Nothing here is a grant: the
// SERVER computes the XP when it arrives, and the numbers shown meanwhile are
// this device's estimate, marked `pending`.
const OUTBOX = 'va.pendingPlays'
interface PendingPlay {
  uid: string
  date: string
  useBoost: boolean
  result: Omit<PlayResult, 'perQuestion' | 'questions'>
  outcome: SubmitOutcome
}
function readOutbox(): PendingPlay[] {
  try {
    const raw = JSON.parse(localStorage.getItem(OUTBOX) || '[]') as unknown
    return Array.isArray(raw) ? (raw as PendingPlay[]).filter((p) => p && typeof p.uid === 'string' && typeof p.date === 'string') : []
  } catch {
    return []
  }
}
function writeOutbox(list: PendingPlay[]) {
  try { localStorage.setItem(OUTBOX, JSON.stringify(list)) } catch { /* storage blocked */ }
}

// The hours of the day the last few plays landed at, device-local, so the
// drop reminder can default to when this person actually plays. Never sent
// anywhere and never shown; a list of hours is a habit, not a score.
const HOURS_KEY = 'va.playHours'

function notePlayHour() {
  try {
    const raw = JSON.parse(localStorage.getItem(HOURS_KEY) || '[]') as unknown
    const hours = (Array.isArray(raw) ? raw : []).filter((h) => typeof h === 'number').slice(-13)
    hours.push(new Date().getHours())
    localStorage.setItem(HOURS_KEY, JSON.stringify(hours))
  } catch {
    /* fine */
  }
}

/** 'HH:MM' one hour before the player's median play hour, or null with too few plays. */
/** The median hour of the device's recent plays (0-23), or null with fewer
 *  than three. The raw habit, where `usualPlayTime` is the nudge an hour before. */
export function usualPlayHour(): number | null {
  try {
    const raw = JSON.parse(localStorage.getItem(HOURS_KEY) || '[]') as unknown
    const hours = (Array.isArray(raw) ? raw : []).filter((h): h is number => typeof h === 'number')
    if (hours.length < 3) return null
    const sorted = [...hours].sort((a, b) => a - b)
    return sorted[Math.floor(sorted.length / 2)]
  } catch {
    return null
  }
}

export function usualPlayTime(): string | null {
  try {
    const raw = JSON.parse(localStorage.getItem(HOURS_KEY) || '[]') as unknown
    const hours = (Array.isArray(raw) ? raw : []).filter((h): h is number => typeof h === 'number')
    if (hours.length < 3) return null
    const sorted = [...hours].sort((a, b) => a - b)
    const median = sorted[Math.floor(sorted.length / 2)]
    // An hour before, so the nudge arrives ahead of the habit rather than
    // after it — and never in the small hours.
    const h = Math.max(7, Math.min(21, median - 1))
    return `${String(h).padStart(2, '0')}:00`
  } catch {
    return null
  }
}

// The daily drop is ALWAYS the global verse — for church members too (0128).
// A verified church's verse used to stand in for it (0119); it is its own lane
// now (`store/churchVerse.ts`, `/play/church`), so the shared moment of the day
// is the same for everybody and the church's reading is added, never swapped.

export const useGame = create<GameState>((set, get) => ({
  today: null,
  todayDate: todayLocalDate(),
  playedToday: false,
  lastResult: null,
  pulse: null,
  boostArmed: false,

  async loadToday() {
    const date = todayLocalDate()
    const verse = getVerseForDate(date)
    const mode = useAuth.getState().mode

    if (mode === 'local' || !supabase) {
      const existing = localdb.getPlay(date)
      set({
        today: verse,
        todayDate: date,
        playedToday: !!existing,
        lastResult: existing,
      })
      return
    }

    // A play that finished offline goes now that we're trying the network again.
    void get().flushPending()

    // ONLINE: make sure the shared row exists, and — in the same round trip,
    // not after it — check if the user played. The read doesn't need the row.
    const sb = supabase
    const ensure = sb.rpc('ensure_daily_verse', {
      p_drop_date: date,
      p_translation: verse.translation,
      p_reference: verse.reference,
      p_book: verse.book,
      p_chapter: verse.chapter,
      p_verse_start: verse.verseStart,
      p_verse_end: verse.verseEnd ?? null,
      p_text: verse.text,
      p_theme: verse.theme ?? null,
      p_questions: verse.questions,
      p_facts: verse.facts,
    })
    const uid = await sessionUserId()
    const [, playRes] = await Promise.all([
      ensure,
      uid
        ? sb.from('plays')
            .select('score, time_ms, correct_count, total_questions, combo_max, xp_earned')
            .eq('user_id', uid)
            .eq('drop_date', date)
            .maybeSingle()
        : Promise.resolve({ data: null }),
    ])
    let played = false
    let restored: GameState['lastResult'] = null
    if (uid) {
      const play = playRes.data as any
      if (play) {
        played = true
        // Rebuild lastResult from the stored play so "See my recap & share" works
        // after a reload or on a return visit — otherwise the recap screen has no
        // in-memory result and renders nothing. perQuestion isn't persisted (the
        // recap doesn't use it); the outcome's streak/level come from the profile.
        const prof = useAuth.getState().profile
        restored = {
          result: {
            score: play.score,
            timeMs: play.time_ms,
            correctCount: play.correct_count,
            totalQuestions: play.total_questions,
            comboMax: play.combo_max,
            perQuestion: [],
          },
          outcome: {
            alreadyPlayed: true,
            xpEarned: play.xp_earned ?? 0,
            xp: prof?.xp ?? 0,
            level: prof?.level ?? 1,
            leveledUp: false,
            currentStreak: prof?.currentStreak ?? 0,
            usedFreeze: false,
            streakFreezes: prof?.streakFreezes ?? 0,
          },
        }
      }
    }
    // Played offline and not sent yet: it is still today's play, so the drop
    // stays done (and can't be dealt again) and the recap still has its numbers.
    const waiting = uid ? readOutbox().find((p) => p.uid === uid && p.date === date) : undefined
    if (!played && waiting) {
      played = true
      restored = { result: { ...waiting.result, perQuestion: [] }, outcome: waiting.outcome }
    }
    // Keep an accurate just-played result if we have one; else use the rebuilt one.
    set({ today: verse, todayDate: date, playedToday: played, lastResult: get().lastResult ?? restored })
  },

  async submitPlay(result) {
    const date = get().todayDate
    const auth = useAuth.getState()
    const useBoost = get().boostArmed

    if (auth.mode === 'local' || !supabase) {
      const prof = auth.profile
      if (!prof) throw new Error('No profile')
      const { profile, outcome } = applyPlayLocal(prof, {
        dropDate: date,
        score: result.score,
        correct: result.correctCount,
        useBoost,
      })
      auth.setProfileLocal(profile)
      localdb.savePlay(date, result, outcome)
      // Log this guest's play so the ambient "opened today" pulse reflects
      // everyone, not just signed-in users, AND so guests appear on the
      // worldwide leaderboard (ranked by their cumulative XP). Fire-and-forget;
      // the RPC is callable by anonymous clients and dedupes per device. We send
      // the POST-play profile so xp/level reflect the score just earned.
      if (supabase) {
        supabase
          .rpc('record_guest_open', {
            p_drop_date: date,
            p_guest_id: localdb.getGuestId(),
            p_username: profile.username,
            p_emoji: profile.avatarEmoji,
            p_score: result.score,
            p_xp: profile.xp,
            p_level: profile.level,
          })
          .then(
            () => {},
            () => {},
          )
      }
      notePlayHour()
      set({ playedToday: true, lastResult: { result, outcome }, boostArmed: false })
      return outcome
    }

    // An earlier offline play goes first, so the server sees the days in order.
    await get().flushPending()
    const { data, error } = await supabase.rpc('submit_play', {
      p_drop_date: date,
      p_score: result.score,
      p_time_ms: result.timeMs,
      p_correct: result.correctCount,
      p_total: result.totalQuestions,
      p_combo_max: result.comboMax,
      p_use_boost: useBoost,
    })
    if (error && isNetworkFailure(error) && auth.profile) {
      const uid = auth.profile.id
      const { outcome: est } = applyPlayLocal(auth.profile, {
        dropDate: date, score: result.score, correct: result.correctCount, useBoost,
      })
      const outcome: SubmitOutcome = { ...est, pending: true }
      const { score, timeMs, correctCount, totalQuestions, comboMax } = result
      writeOutbox([
        ...readOutbox().filter((p) => !(p.uid === uid && p.date === date)),
        { uid, date, useBoost, result: { score, timeMs, correctCount, totalQuestions, comboMax }, outcome },
      ])
      notePlayHour()
      set({ playedToday: true, lastResult: { result, outcome }, boostArmed: false })
      return outcome
    }
    if (error) throw error
    const outcome = normalizeOutcome(data)
    await auth.refreshProfile()
    notePlayHour()
    set({ playedToday: true, lastResult: { result, outcome }, boostArmed: false })
    return outcome
  },

  async loadPulse() {
    const date = get().todayDate
    if (!supabase) {
      // LOCAL mode: synthesize a warm, believable ambient pulse so solo play
      // still feels populated. (Clearly fake data; real counts come online.)
      set({ pulse: synthPulse(date) })
      return
    }
    const { data, error } = await supabase.rpc('get_daily_pulse', { p_drop_date: date })
    if (error) return
    const raw = data as { opened: number; feed: any[] }
    set({
      pulse: {
        opened: raw.opened,
        feed: raw.feed.map((f) => ({
          username: f.username,
          avatarEmoji: f.avatar_emoji,
          points: f.points,
          kind: f.kind,
          createdAt: f.created_at,
          avatarBorder: f.avatar_border ?? 'default',
          avatarBadge: f.avatar_badge ?? null,
        })),
      },
    })
  },

  armBoost(v) {
    set({ boostArmed: v })
  },

  flushPending: shared(async () => {
    if (!supabase || useAuth.getState().mode !== 'online') return
    const uid = await sessionUserId()
    if (!uid) return
    const mine = readOutbox().filter((p) => p.uid === uid).sort((a, b) => a.date.localeCompare(b.date))
    if (!mine.length) return
    let sent = 0
    for (const p of mine) {
      const { data, error } = await supabase.rpc('submit_play', {
        p_drop_date: p.date,
        p_score: p.result.score,
        p_time_ms: p.result.timeMs,
        p_correct: p.result.correctCount,
        p_total: p.result.totalQuestions,
        p_combo_max: p.result.comboMax,
        p_use_boost: p.useBoost,
      })
      // Still offline: keep it and try again later. Anything else — sent, or
      // refused — is settled, and a refused play is not retried forever.
      if (error && isNetworkFailure(error)) break
      writeOutbox(readOutbox().filter((q) => !(q.uid === uid && q.date === p.date)))
      sent++
      const last = get().lastResult
      if (!error && p.date === get().todayDate && last?.outcome.pending) {
        set({ lastResult: { result: last.result, outcome: normalizeOutcome(data) } })
      }
    }
    if (sent) await useAuth.getState().refreshProfile()
  }),
}))

// Back online: send what waited. (Also sent on every Play-tab visit.)
if (typeof window !== 'undefined') {
  // Twice, in sequence, for the outbox's reason (lib/outbox.ts): a flush begun
  // while still offline may be in flight right now.
  window.addEventListener('online', () => {
    void useGame.getState().flushPending().then(() => useGame.getState().flushPending())
  })
}

function normalizeOutcome(d: any): SubmitOutcome {
  return {
    alreadyPlayed: !!d.already_played,
    xpEarned: d.xp_earned,
    xp: d.xp,
    level: d.level,
    leveledUp: !!d.leveled_up,
    currentStreak: d.current_streak,
    usedFreeze: !!d.used_freeze,
    streakFreezes: d.streak_freezes,
    boostUsed: !!d.boost_used,
    xpBoosts: d.xp_boosts,
  }
}

// Believable filler for offline/solo so "you're not alone" still lands.
const NAMES = ['grace_r', 'j_walks', 'psalmsurfer', 'noah_b', 'lightseeker', 'ruth88', 'danielc', 'ekklesia', 'maranatha', 'olivebranch', 'shalomkid', 'the_esther', 'ziontrail', 'abba_reads']
const EMOJI = ['📖', '🕊️', '✨', '🌿', '🔥', '⭐', '🙏', '🌅']
// Seeded by the date and walked forward by the hour (2026-10-29): it used to be
// Math.random(), so "2,307 played today" read 2,258 on the next reload and
// 2,349 on the one after — plainly invented. Now one day's count only grows,
// and the same on every reload. Keyless LOCAL builds only; online is real.
function synthPulse(date: string): DailyPulse {
  const rng = mulberry32(hashString(`pulse:${date}`))
  const hour = new Date().getHours()
  const feed = Array.from({ length: 14 }).map((_, i) => ({
    username: NAMES[i % NAMES.length],
    avatarEmoji: EMOJI[i % EMOJI.length],
    points: 400 + Math.floor(rng() * 900),
    kind: 'scored' as const,
    createdAt: new Date(Date.now() - i * 42000).toISOString(),
  }))
  return { opened: 600 + Math.floor(rng() * 400) + hour * 70, feed }
}
